import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import Heading from "../../ui/Heading";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { DataTable } from "../../components/ui/datatable";
import { FaLongArrowAltRight, FaLongArrowAltLeft } from "react-icons/fa";

import { useOptimisticAdd } from "../../hooks/useOptimisticAdd";
import { addUserUploadedDocument } from "../../services/apiUser";
import { Card } from "@/components/ui/card";
import { documentUploadColumns } from "../business_profile/DocumentUploadColumns";
import { useGetUploadDocuments } from "../business_profile/useGetUploadDocuments.js";

const FormSchema = z.object({
  type: z.string({ required_error: "Please select a document type." }),
  file: z
    .any()
    .refine((file) => file, "Please upload a document.")
    .refine(
      (file) => {
        if (!file) return true;
        const fileType = file.type;
        return (
          fileType === "application/pdf" ||
          fileType ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
          fileType === "application/msword"
        );
      },
      {
        message: "Only pdf, docx, and doc files are allowed.",
      }
    ),
});

function IndexedDocumentsTable() {
  const { data, isLoading, isSuccess } = useGetUploadDocuments();

  if (!isSuccess || !data || data.length === 0) {
    return null; // Return nothing if there are no documents or the data is still loading
  }

  const filteredData = data.filter((d) => d.type !== "Business Profile");

  if (filteredData.length === 0) {
    return null; // Return nothing if filteredData has no documents
  }

  return (
    <div>
      <Heading as="h2" className="font-bold mb-2">
        Indexed Documents
      </Heading>
      <p className="text-muted-foreground mb-4">
        Review and manage your uploaded documents below.
      </p>
      <div id="cp-profile-doc-upload-table">
        <DataTable
          columns={documentUploadColumns}
          className={"bg-white"}
          data={filteredData}
          filterColumn="title"
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}

function FilesForm({ onStepComplete, onStepPrevious }) {
  const { mutate, data: uploadedDocuments = [] } = useOptimisticAdd(
    "uploadedDocuments",
    addUserUploadedDocument
  );

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      file: null,
      type: "",
    },
  });

  function onSubmit(data) {
    const newData = {
      title: data.file.name,
      type: data.type,
      status: "Processing",
    };
    const addData = {
      file: data.file,
      type: data.type,
    };
    mutate({ newData, addData });
    form.reset();
  }

  useEffect(() => {
    const { file, type } = form.getValues();
    if (file && type) {
      form.handleSubmit(onSubmit)();
    }
  }, [form.watch("file"), form.watch("type")]);

  return (
    <Card className="w-full max-w-4xl space-y-8 p-6 rounded-lg border bg-white text-black shadow-md">
      <Form {...form}>
        <form className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold mb-2 text-black">Upload your documents</h1>
            <div className="bg-blue-50 p-4 rounded-lg text-sm text-gray-700 mb-6">
            <p>
              By uploading your documents, you're building a knowledge base that our AI will use to craft customized government contract proposals. These documents provide critical insights that the AI will reference to create tailored proposals. The more comprehensive and relevant your documents, the better SamSearch can assist in winning contracts for your business.
            </p>
            <p>
              <strong>Without this knowledge, the quality of the proposals will be significantly lower.</strong>
            </p>

            </div>
          </div>

          <FormField
            control={form.control}
            name="file"
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel className="text-black">Upload File</FormLabel>
                <FormDescription className="text-gray-500">
                  Click or drag to upload your file.
                </FormDescription>
                <FormControl>
                  <Input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="file:border-none file:bg-blue-300 file:text-white file:px-3 file:py-1 file:rounded-lg file:w-auto file:cursor-pointer hover:file:bg-blue-600 dark:file:bg-blue-600 dark:file:text-white dark:hover:file:bg-blue-700 bg-white text-black"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      field.onChange(file);
                      form.setValue("file", file);
                    }}
                  />
                </FormControl>
                {error && <FormMessage>{error.message}</FormMessage>}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-black">Select Document Type</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-white text-black">
                      <SelectValue placeholder="Select the type of your document." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-white text-black">
                    <SelectItem value="Past Performance Records">
                      Past Performance Records
                    </SelectItem>
                    <SelectItem value="Capabilities Statement">
                      Capabilities Statement
                    </SelectItem>
                    <SelectItem value="Industry Certifications and Licenses">
                      Industry Certifications and Licenses
                    </SelectItem>
                    <SelectItem value="Compliance Documentation">
                      Compliance Documentation
                    </SelectItem>
                    <SelectItem value="Organizational Knowledge Repository">
                      Organizational Knowledge Repository
                    </SelectItem>
                    <SelectItem value="Case Studies and White Papers">
                      Case Studies and White Papers
                    </SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
      <IndexedDocumentsTable />
      <div className="flex justify-between">
        <Button
          type="button"
          onClick={onStepPrevious}
        >
          <FaLongArrowAltLeft className="mr-2" />
          Previous
        </Button>
        <Button
          type="button"
          onClick={onStepComplete}
        >
          Next
          <FaLongArrowAltRight className="ml-2" />
        </Button>
      </div>
    </Card>
  );
}

export default FilesForm;
